import React, { useEffect, useState, useRef } from 'react';
import {
  Modal,
  Form,
  Input,
  Row,
  Col,
  notification,
  DatePicker,
  Upload,
  Select,
} from 'antd';
import { CloudUploadOutlined, DownOutlined } from '@ant-design/icons';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button as CrcButton } from '@crc-evans/ui-components';
import { OnAddMaterial, OnUpdateMaterialData } from '../../redux/materials/saga';
import { fetchMaterialCategories } from '../../redux/materialConsumption/action';
import axiosConfig from '../../services/axiosConfig';
import { get } from 'lodash';
import './MaterialsModal.css';

const { TextArea } = Input;

/**
 * Custom styled select for modern theme (matches design system).
 */
// Initial form state
const initialFormData = {
  MaterialCategory: '',
  Name: '',
  ProductCode: '',
  Datasheet: '',
  ManufactureName: '',
  TradeName: '',
  Applications: undefined,
  Reference: '',
  RevisionDate: null,
  FormerDate: null,
  versionOrRevisionNumber: '',
  StorageTemperature: '',
  Comments: '',
  AvailabilityStatus: undefined,
};

const MaterialsModal = (props) => {
  const {
    isModalVisible,
    setIsModalVisible,
    materialForm,
    isEdit,
    setIsEdit,
    editRecord,
    isAddUpdateMaterial,
    fetchMaterialsData,
    categoryData,
    onFetchCategories,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [categoryDataList, setCategoryDataList] = useState([]);
  const [filteredCatg, setFilteredCatg] = useState([]);
  const [filteredProductCode, setFilteredProductCode] = useState([]);

  // Applications dropdown options
  const applicationOptions = [
    'Thermal Coating',
    'Thin Film Coating',
    'Anticorrossive',
    'Abrasive',
    'Additive',
  ];

  // Availability Status options
  const availabilityStatusOptions = [
    'Available',
    'Restricted Supply',
    'Discontinued',
  ];

  // Datasheet options
  const datasheetOptions = ['MDS', 'SDS', 'EDS', 'Handling & Storage', 'Bulletin', 'Others'];

  // Fetch categories when modal opens
  useEffect(() => {
    if (isModalVisible && onFetchCategories) {
      onFetchCategories();
    }
  }, [isModalVisible, onFetchCategories]);

  // Process unique categories
  useEffect(() => {
    if (categoryData && categoryData.length) {
      const uniqueCategory = new Set();
      const categoryArr = categoryData.filter((item) => {
        const isDuplicate = uniqueCategory.has(item.category);
        uniqueCategory.add(item.category);
        return !isDuplicate;
      });
      setCategoryDataList(categoryArr);
    } else {
      setCategoryDataList([]);
    }
  }, [categoryData]);

  // Handle category change - filter names
  const onChangeCategory = (value) => {
    materialForm.setFieldsValue({ Name: '', ProductCode: '' });
    handleInputChange('MaterialCategory', value);
    handleInputChange('Name', '');
    handleInputChange('ProductCode', '');

    if (value) {
      var categoryArr = categoryData && categoryData.filter((item) => item.category == value);

      // Use a Set to remove duplicates based on name
      const uniqueName = new Set();
      categoryArr = categoryArr.filter((item) => {
        const isDuplicate = uniqueName.has(item.name);
        uniqueName.add(item.name);
        return !isDuplicate;
      });

      setFilteredCatg(categoryArr);
    } else {
      setFilteredCatg([]);
      setFilteredProductCode([]);
    }
  };

  // Handle name change - filter product codes
  const onChangeName = (value) => {
    materialForm.setFieldsValue({ ProductCode: '' });
    handleInputChange('Name', value);
    handleInputChange('ProductCode', '');

    if (value) {
      var categoryArr = filteredCatg && filteredCatg.filter((item) => item.name == value);

      // Use a Set to remove duplicates based on productCode
      const uniqueProductCode = new Set();
      categoryArr = categoryArr.filter((item) => {
        const isDuplicate = uniqueProductCode.has(item.productCode);
        uniqueProductCode.add(item.productCode);
        return !isDuplicate;
      });

      setFilteredProductCode(categoryArr);
    } else {
      setFilteredProductCode([]);
    }
  };

  useEffect(() => {
    if (isEdit && editRecord) {
      const versionOrRevisionNumber = editRecord.versionOrRevisionNumber
        ? String(editRecord.versionOrRevisionNumber).trim()
        : '';

      const editData = {
        MaterialCategory: editRecord.MaterialCategory || '',
        Name: editRecord.Name || '',
        ProductCode: editRecord.ProductCode || '',
        Datasheet: editRecord.Datasheet || '',
        ManufactureName: editRecord.ManufactureName || '',
        TradeName: editRecord.TradeName || '',
        Applications: editRecord.Applications || '',
        Reference: editRecord.Reference || '',
        RevisionDate: editRecord.RevisionDate ? moment(editRecord.RevisionDate) : null,
        FormerDate: editRecord.FormerDate ? moment(editRecord.FormerDate) : null,
        versionOrRevisionNumber,
        StorageTemperature: editRecord.StorageTemperature || '',
        Comments: editRecord.Comments || '',
        AvailabilityStatus: editRecord.AvailabilityStatus || undefined,
      };

      setFormData(editData);
      materialForm.setFieldsValue(editData);

      // Set filtered data for edit mode
      if (editRecord.MaterialCategory && categoryData && categoryData.length) {
        const categoryArr = categoryData.filter((item) => item.category == editRecord.MaterialCategory);
        const uniqueName = new Set();
        const filteredNames = categoryArr.filter((item) => {
          const isDuplicate = uniqueName.has(item.name);
          uniqueName.add(item.name);
          return !isDuplicate;
        });
        setFilteredCatg(filteredNames);

        if (editRecord.Name) {
          const nameArr = filteredNames.filter((item) => item.name == editRecord.Name);
          const uniqueProductCode = new Set();
          const filteredCodes = nameArr.filter((item) => {
            const isDuplicate = uniqueProductCode.has(item.productCode);
            uniqueProductCode.add(item.productCode);
            return !isDuplicate;
          });
          setFilteredProductCode(filteredCodes);
        }
      }
    } else {
      setFormData(initialFormData);
      materialForm.resetFields();
      setFilteredCatg([]);
      setFilteredProductCode([]);
    }
  }, [isEdit, editRecord, isModalVisible, materialForm, categoryData]);

  const fileProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    fileList,
  };

  const handleInputChange = (name, value) => {
    if (name === 'RevisionDate' || name === 'FormerDate') {
      value = value ? moment(value).format('YYYY-MM-DD') : null;
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleCancel = () => {
    materialForm.resetFields();
    setIsEdit(false);
    setFormData(initialFormData);
    setFileList([]);
    setFilteredCatg([]);
    setFilteredProductCode([]);
    setIsModalVisible(false);
  };

  // Handle certificate download/view
  const handleCertificateView = async (certificate, materialId) => {
    try {
      if (!certificate || !certificate.id || !materialId) {
        notification.error({
          message: 'Invalid certificate information',
        });
        return;
      }

      // Call API to get presigned URL
      const response = await axiosConfig.get(`/materials-module/${materialId}/download-certificate`, {
        params: {
          certificateId: certificate.id,
        },
      });

      const presignedUrl = get(response, ['data', 'data', 'presignedUrl'], null) ||
        get(response, ['data', 'data', 'url'], null) ||
        get(response, ['data', 'presignedUrl'], null) ||
        get(response, ['data', 'url'], null);

      if (presignedUrl) {
        // Open in new tab
        window.open(presignedUrl, '_blank');
      } else {
        notification.error({
          message: 'Failed to get certificate URL',
        });
      }
    } catch (error) {
      console.error('Error viewing certificate:', error);
      notification.error({
        message: 'Failed to view certificate',
        description: get(error, ['response', 'data', 'message'], error.message || 'An error occurred'),
      });
    }
  };

  const getCategoryValue = (type) => {
    if (!formData.hasOwnProperty(type)) return undefined;
    const value = formData[type];
    if (type === 'versionOrRevisionNumber') {
      return value !== undefined && value !== null ? String(value) : '';
    }
    return value;
  };

  const handleOk = async () => {
    try {
      await materialForm.validateFields();

      // Check for empty required fields
      const requiredFields = {
        Datasheet: 'Datasheet',
        ManufactureName: 'Manufacture Name',
        TradeName: 'Trade Name',
        RevisionDate: 'Revision Date',
        versionOrRevisionNumber: 'Version / Revision Number',
      };

      setIsLoading(true);

      for (const field in requiredFields) {
        if (!formData[field]) {
          notification.warning({
            message: `Please fill the "${requiredFields[field]}" field to proceed!`,
          });
          setIsLoading(false);
          return;
        }
      }

      if (!isEdit && fileList.length === 0) {
        notification.warning({
          message: 'Please select data sheet file to proceed!',
        });
        setIsLoading(false);
        return;
      }

      // Format the payload according to API requirements
      const submitData = {
        MaterialCategory: formData.MaterialCategory || '',
        Name: formData.Name || '',
        ProductCode: formData.ProductCode || '',
        Datasheet: formData.Datasheet || '',
        ManufactureName: formData.ManufactureName || '',
        TradeName: formData.TradeName || '',
        Applications: formData.Applications || '',
        Reference: formData.Reference || '',
        RevisionDate: formData.RevisionDate
          ? moment(formData.RevisionDate).format('YYYY-MM-DD') + 'T00:00:00.000Z'
          : null,
        FormerDate: formData.FormerDate
          ? moment(formData.FormerDate).format('YYYY-MM-DD') + 'T00:00:00.000Z'
          : null,
        versionOrRevisionNumber: formData.versionOrRevisionNumber || '',
        StorageTemperature: formData.StorageTemperature || '',
        Comments: formData.Comments || '',
        AvailabilityStatus: formData.AvailabilityStatus || '',
        dataSheet: fileList, // Pass file list for upload (similar to certificate in calibration)
      };

      const res = isEdit && editRecord?.id
        ? await OnUpdateMaterialData({ data: submitData, id: editRecord.id })
        : await OnAddMaterial({ data: submitData });

      setIsLoading(false);

      if (res === false) {
        setIsEdit(false);
        setIsModalVisible(false);
        materialForm.resetFields();
        setFileList([]);
        setFormData(initialFormData);
        if (fetchMaterialsData) {
          fetchMaterialsData();
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      width={1000}
      open={isModalVisible}
      title={isEdit ? 'Update Material' : 'New Material'}
      className="materials-modal-modern heatprofile-modal pipeline-modal trackrecord-modal Tracker-modal"
      footer={null}
      closable
      onCancel={handleCancel}
    >
      <Form
        form={materialForm}
        name="material_form"
        layout="vertical"
        autoComplete="off"
        getContainer={false}
      >
        <Row gutter={24}>
          <Col lg={6}>
            <Form.Item name="MaterialCategory" label="Material Category">
              <Select
                placeholder="Select Material Category"
                allowClear
                showSearch
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownClassName="heat-profile-select-dropdown"
                onChange={(value) => {
                  onChangeCategory(value);
                  materialForm.setFieldsValue({ MaterialCategory: value ?? undefined });
                }}
              >
                {categoryDataList ? categoryDataList.map((item) => (
                  <Select.Option key={item?.category} value={item?.category}>
                    {item?.category}
                  </Select.Option>
                )) : []}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="Name" label="Name">
              <Select
                placeholder="Select Name"
                disabled={!filteredCatg.length}
                allowClear
                showSearch
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownClassName="heat-profile-select-dropdown"
                onChange={(value) => {
                  onChangeName(value);
                  materialForm.setFieldsValue({ Name: value ?? undefined });
                }}
              >
                {filteredCatg ? filteredCatg.map((item) => (
                  <Select.Option key={item?.name} value={item?.name}>
                    {item?.name}
                  </Select.Option>
                )) : []}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="ProductCode" label="Product Code">
              <Select
                placeholder="Select Product Code"
                disabled={!formData.Name}
                allowClear
                showSearch
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownClassName="heat-profile-select-dropdown"
                onChange={(value) => {
                  handleInputChange('ProductCode', value);
                  materialForm.setFieldsValue({ ProductCode: value ?? undefined });
                }}
              >
                {filteredProductCode ? filteredProductCode.map((item) => (
                  <Select.Option key={item?.productCode} value={item?.productCode}>
                    {item?.productCode}
                  </Select.Option>
                )) : []}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="Datasheet" label="Datasheet" required>
              <Select
                placeholder="Select Datasheet"
                allowClear
                showSearch
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownClassName="heat-profile-select-dropdown"
                onChange={(value) => {
                  handleInputChange('Datasheet', value);
                  materialForm.setFieldsValue({ Datasheet: value ?? undefined });
                }}
              >
                {(datasheetOptions || []).map((opt) => (
                  <Select.Option key={opt} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="ManufactureName" label="Manufacture Name" required>
              <div>
                <Input
                  onChange={(e) => handleInputChange('ManufactureName', e.target.value)}
                  size="large"
                  value={getCategoryValue('ManufactureName')}
                  placeholder="Enter Manufacture Name"
                />
              </div>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="TradeName" label="Trade Name" required>
              <div>
                <Input
                  onChange={(e) => handleInputChange('TradeName', e.target.value)}
                  size="large"
                  value={getCategoryValue('TradeName')}
                  placeholder="Enter Trade Name"
                />
              </div>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="Applications" label="Applications">
              <Select
                placeholder="Select Applications"
                allowClear
                showSearch
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownClassName="heat-profile-select-dropdown"
                onChange={(value) => {
                  handleInputChange('Applications', value);
                  materialForm.setFieldsValue({ Applications: value ?? undefined });
                }}
              >
                {(applicationOptions || []).map((opt) => (
                  <Select.Option key={opt} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="Reference" label="Reference">
              <div>
                <Input
                  onChange={(e) => handleInputChange('Reference', e.target.value)}
                  size="large"
                  value={getCategoryValue('Reference')}
                  placeholder="Enter Reference"
                />
              </div>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="RevisionDate" label="Revision Date" required>
              <div>
                <DatePicker
                  onChange={(date) => handleInputChange('RevisionDate', date)}
                  size="large"
                  value={getCategoryValue('RevisionDate') ? moment(getCategoryValue('RevisionDate')) : null}
                  placeholder="Select Revision Date"
                  format="DD-MMM-YYYY"
                  style={{ width: '100%' }}
                  className="materials-date-picker"
                />
              </div>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="FormerDate" label="Former Date">
              <div>
                <DatePicker
                  onChange={(date) => handleInputChange('FormerDate', date)}
                  size="large"
                  value={getCategoryValue('FormerDate') ? moment(getCategoryValue('FormerDate')) : null}
                  placeholder="Select Former Date"
                  format="DD-MMM-YYYY"
                  style={{ width: '100%' }}
                  className="materials-date-picker"
                />
              </div>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item
              name="versionOrRevisionNumber"
              label="Version / Revision Number"
              required
            >
              <div>
                <Input
                  onChange={(e) => handleInputChange('versionOrRevisionNumber', e.target.value)}
                  size="large"
                  value={getCategoryValue('versionOrRevisionNumber') || ''}
                  placeholder="Enter Version / Revision Number"
                />
              </div>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="StorageTemperature" label="Storage Temperature (°C)">
              <div>
                <Input
                  onChange={(e) => handleInputChange('StorageTemperature', e.target.value)}
                  size="large"
                  value={getCategoryValue('StorageTemperature')}
                  placeholder="Enter Storage Temperature"
                />
              </div>
            </Form.Item>
          </Col>
          <Col lg={6}>
            <Form.Item name="AvailabilityStatus" label="Availability Status">
              <Select
                placeholder="Select Availability Status"
                allowClear
                showSearch
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownClassName="heat-profile-select-dropdown"
                onChange={(value) => {
                  handleInputChange('AvailabilityStatus', value);
                  materialForm.setFieldsValue({ AvailabilityStatus: value ?? undefined });
                }}
              >
                {(availabilityStatusOptions || []).map((opt) => (
                  <Select.Option key={opt} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={24}>
            <Form.Item name="Comments" label="Comments">
              <div>
                <TextArea
                  onChange={(e) => handleInputChange('Comments', e.target.value)}
                  size="large"
                  value={getCategoryValue('Comments')}
                  placeholder="Enter Comments"
                  rows={4}
                />
              </div>
            </Form.Item>
          </Col>

          <Col lg={24}>
            <div style={{ marginBottom: '10px', marginTop: '-25px' }}>
              <div className="heatp-bottom-border">&nbsp;</div>
            </div>
          </Col>
        </Row>

        <div style={{ paddingTop: '20px' }}>
          <Col lg={8}>
            <Form.Item name="dataSheet" label="Upload Data Sheet" required={!isEdit}>
              <Upload
                {...fileProps}
                maxCount={1}
                accept=".doc,.docx,application/pdf"
                className="upload-full-width"
                showUploadList={false}
              >
                <div className="premium-upload-trigger">
                  <CloudUploadOutlined className="upload-icon" />
                  <div className="upload-text">
                    <span className="main-text">Click to Select Data Sheet</span>
                    <span className="sub-text">Support for .doc, .docx, .pdf files</span>
                  </div>
                </div>
              </Upload>
              {fileList.length > 0 && (
                <div style={{ marginTop: '8px', color: '#4B5563', fontSize: '14px' }}>
                  Selected: {fileList[0].name}
                </div>
              )}
            </Form.Item>
          </Col>
          {isEdit && editRecord && editRecord.certificates && Array.isArray(editRecord.certificates) && editRecord.certificates.length > 0 && (
            <Col lg={16}>
              <Form.Item label="Certificates">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {editRecord.certificates
                    .sort((a, b) => (b.fileVersion || 0) - (a.fileVersion || 0)) // Sort by version descending
                    .map((certificate, index) => {
                      const isLatest = index === 0; // First item after sorting is latest
                      const maxVersion = editRecord.certificates.reduce((max, cert) =>
                        Math.max(max, cert.fileVersion || 0), 0
                      );
                      const isLatestVersion = certificate.fileVersion === maxVersion;

                      return (
                        <li key={certificate.id || index} style={{ marginBottom: '8px' }}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleCertificateView(certificate, editRecord.id || editRecord.ID);
                            }}
                            style={{
                              color: '#1890ff',
                              textDecoration: 'none',
                            }}
                          >
                            Certificate Version {certificate.fileVersion || 'N/A'}
                            {isLatestVersion && (
                              <span style={{
                                marginLeft: '8px',
                                color: '#52c41a',
                                fontWeight: 'bold',
                                fontSize: '12px'
                              }}>
                                (Latest)
                              </span>
                            )}
                          </a>
                        </li>
                      );
                    })}
                </ul>
              </Form.Item>
            </Col>
          )}
        </div>

        <div className="materials-modal-modern-footer">
          <CrcButton
            key="cancel"
            label="Cancel"
            onClick={handleCancel}
            variant="tertiary"
            size="medium"
          />
          <CrcButton
            key="apply"
            label={
              isEdit
                ? isLoading || isAddUpdateMaterial
                  ? 'Updating...'
                  : 'Update'
                : isLoading || isAddUpdateMaterial
                  ? 'Submitting...'
                  : 'Submit'
            }
            onClick={handleOk}
            disabled={isLoading || isAddUpdateMaterial}
            variant="primary"
            size="medium"
          />
        </div>
      </Form>
    </Modal>
  );
};

const mapStateToProps = ({ materialsReducer, materialConsumptionReducer }) => {
  const categoryData = materialConsumptionReducer?.materialCategories || [];
  return {
    isAddUpdateMaterial: materialsReducer?.isAddUpdateMaterial || false,
    categoryData,
  };
};

const mapDispatchToProps = {
  onFetchCategories: fetchMaterialCategories,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaterialsModal);
